import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  addDoc
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { POD_COLLECTION } from '../config/collections';
import {
  MeterReading,
  MeterPoint,
  ConsumptionLocation,
  LocationAddress,
  MeterReadingFilter,
  MeterReadingSummary,
  SettlementStatus,
  AllowedStatusTransitions
} from '../types/meterReading';

/**
 * Mérőállás Kezelési Service
 * Firebase-ból olvassa ki a Python backend által feltöltött adatokat
 */
class MeterReadingService {
  
  /**
   * Mérőállások lekérése szűrőkkel
   */
  async getMeterReadings(filter: MeterReadingFilter = {}): Promise<MeterReading[]> {
    try {
      let q = query(collection(db, 'meter_readings'));
      
      // Szűrők alkalmazása
      if (filter.customerId) {
        q = query(q, where('customerId', '==', filter.customerId));
      }
      
      if (filter.podId) {
        q = query(q, where('podId', '==', filter.podId));
      }
      
      if (filter.energyType) {
        q = query(q, where('energyType', '==', filter.energyType));
      }
      
      if (filter.settlementStatus) {
        q = query(q, where('settlementStatus', '==', filter.settlementStatus));
      }
      
      if (filter.needsReview !== undefined) {
        q = query(q, where('needsReview', '==', filter.needsReview));
      }
      
      if (filter.startDate) {
        q = query(q, where('readingDate', '>=', Timestamp.fromDate(filter.startDate)));
      }
      
      if (filter.endDate) {
        q = query(q, where('readingDate', '<=', Timestamp.fromDate(filter.endDate)));
      }
      
      // Rendezés dátum szerint
      q = query(q, orderBy('readingDate', 'desc'));
      
      const snapshot = await getDocs(q);
      const readings: MeterReading[] = [];
      
      snapshot.forEach((doc) => {
        readings.push({
          id: doc.id,
          ...doc.data()
        } as MeterReading);
      });
      
      return readings;
      
    } catch (error) {
      console.error('Error fetching meter readings:', error);
      throw error;
    }
  }
  
  /**
   * Egy mérőállás lekérése ID alapján
   */
  async getMeterReading(readingId: string): Promise<MeterReading | null> {
    try {
      const docRef = doc(db, 'meter_readings', readingId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as MeterReading;
      }
      
      return null;
      
    } catch (error) {
      console.error('Error fetching meter reading:', error);
      throw error;
    }
  }
  
  /**
   * POD mérőállásainak lekérése
   */
  async getMeterReadingsForPod(podId: string, limitCount: number = 100): Promise<MeterReading[]> {
    try {
      const q = query(
        collection(db, 'meter_readings'),
        where('podId', '==', podId),
        orderBy('readingDate', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      const readings: MeterReading[] = [];
      
      snapshot.forEach((doc) => {
        readings.push({
          id: doc.id,
          ...doc.data()
        } as MeterReading);
      });
      
      return readings;
      
    } catch (error) {
      console.error('Error fetching POD meter readings:', error);
      throw error;
    }
  }
  
  /**
   * Ügyfél mérőállásainak összesítése
   */
  async getCustomerMeterReadingSummary(customerId: string): Promise<MeterReadingSummary> {
    try {
      const readings = await this.getMeterReadings({ customerId });
      
      const summary: MeterReadingSummary = {
        totalReadings: readings.length,
        validated: 0,
        needsReview: 0,
        byEnergyType: { electricity: 0, gas: 0 },
        byStatus: {
          '01': 0, '02': 0, '04': 0, '05': 0, '07': 0, '10': 0
        } as Record<SettlementStatus, number>,
        uniquePods: 0,
        pods: []
      };
      
      const podSet = new Set<string>();
      
      readings.forEach(reading => {
        if (reading.validated) summary.validated++;
        if (reading.needsReview) summary.needsReview++;
        
        summary.byEnergyType[reading.energyType]++;
        summary.byStatus[reading.settlementStatus]++;
        
        podSet.add(reading.podId);
      });
      
      summary.pods = Array.from(podSet);
      summary.uniquePods = podSet.size;
      
      return summary;
      
    } catch (error) {
      console.error('Error getting customer meter reading summary:', error);
      throw error;
    }
  }
  
  /**
   * Elszámolás státusz frissítése
   */
  async updateSettlementStatus(
    readingId: string,
    newStatus: SettlementStatus,
    userId: string
  ): Promise<boolean> {
    try {
      // Lekérjük a jelenlegi mérőállást
      const reading = await this.getMeterReading(readingId);
      
      if (!reading) {
        throw new Error('Mérőállás nem található');
      }
      
      // Ellenőrizzük, hogy engedélyezett-e a státusz váltás
      const allowedTransitions = AllowedStatusTransitions[reading.settlementStatus];
      
      if (!allowedTransitions.includes(newStatus)) {
        throw new Error(
          `Nem engedélyezett státusz váltás: ${reading.settlementStatus} → ${newStatus}`
        );
      }
      
      // Frissítjük a státuszt
      const docRef = doc(db, 'meter_readings', readingId);
      await updateDoc(docRef, {
        settlementStatus: newStatus,
        modifiedAt: Timestamp.now(),
        modifiedBy: userId
      });
      
      return true;
      
    } catch (error) {
      console.error('Error updating settlement status:', error);
      throw error;
    }
  }
  
  /**
   * Mérőhely lekérése POD alapján
   */
  async getMeterPointByPod(podId: string): Promise<MeterPoint | null> {
    try {
      const q = query(
        collection(db, 'meter_points'),
        where('podId', '==', podId),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data()
        } as MeterPoint;
      }
      
      return null;
      
    } catch (error) {
      console.error('Error fetching meter point:', error);
      throw error;
    }
  }
  
  /**
   * Ügyfél mérőhelyeinek lekérése
   */
  async getCustomerMeterPoints(customerId: string): Promise<MeterPoint[]> {
    try {
      const q = query(
        collection(db, 'meter_points'),
        where('customerId', '==', customerId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const meterPoints: MeterPoint[] = [];
      
      snapshot.forEach((doc) => {
        meterPoints.push({
          id: doc.id,
          ...doc.data()
        } as MeterPoint);
      });
      
      return meterPoints;
      
    } catch (error) {
      console.error('Error fetching customer meter points:', error);
      throw error;
    }
  }
  
  /**
   * Fogyasztási helyek lekérése
   */
  async getConsumptionLocations(customerId: string): Promise<ConsumptionLocation[]> {
    try {
      const q = query(
        collection(db, 'consumption_locations'),
        where('customerId', '==', customerId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const locations: ConsumptionLocation[] = [];
      
      snapshot.forEach((doc) => {
        locations.push({
          id: doc.id,
          ...doc.data()
        } as ConsumptionLocation);
      });
      
      return locations;
      
    } catch (error) {
      console.error('Error fetching consumption locations:', error);
      throw error;
    }
  }
  
  /**
   * Mérőhelyek lekérése fogyasztási hely alapján
   */
  async getMeterPointsByLocation(consumptionLocationId: string): Promise<MeterPoint[]> {
    try {
      const q = query(
        collection(db, 'meter_points'),
        where('consumptionLocationId', '==', consumptionLocationId)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as MeterPoint));
    } catch (error) {
      console.error('Error getting meter points by location:', error);
      throw error;
    }
  }

  /**
   * Fogyasztási hely létrehozása
   */
  async createConsumptionLocation(
    customerId: string,
    locationName: string,
    address: LocationAddress,
    locationType: 'residential' | 'commercial' | 'industrial',
    isActive: boolean = true
  ): Promise<string> {
    try {
      // Generált teljes cím kereséshez
      const fullAddress = `${address.postalCode} ${address.city}, ${address.street} ${address.houseNumber}${
        address.building ? ` ${address.building}` : ''
      }${address.floor ? `, ${address.floor}` : ''}${address.door ? `/${address.door}` : ''}`;

      const locationData = {
        customerId,
        locationName,
        address,
        fullAddress,
        locationType,
        isActive,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      const docRef = await addDoc(collection(db, 'consumption_locations'), locationData);
      return docRef.id;
      
    } catch (error) {
      console.error('Error creating consumption location:', error);
      throw error;
    }
  }
  
  /**
   * Fogyasztási hely törlése
   */
  async deleteConsumptionLocation(locationId: string): Promise<boolean> {
    try {
      console.log('🗑️ Fogyasztási hely törlése, ID:', locationId);
      
      // Ellenőrizzük, hogy vannak-e hozzárendelt mérőhelyek
      const meterPoints = await this.getMeterPointsByLocation(locationId);
      
      if (meterPoints.length > 0) {
        console.warn('⚠️ A fogyasztási helyhez még', meterPoints.length, 'mérőhely tartozik');
        throw new Error(`Nem törölhető! A fogyasztási helyhez még ${meterPoints.length} POD tartozik. Először távolítsd el a POD-okat!`);
      }
      
      // Töröljük a fogyasztási hely dokumentumot
      const docRef = doc(db, 'consumption_locations', locationId);
      await deleteDoc(docRef);
      
      console.log('✅ Fogyasztási hely sikeresen törölve');
      return true;
      
    } catch (error) {
      console.error('❌ Fogyasztási hely törlési hiba:', error);
      throw error;
    }
  }

  /**
   * Mérőhely eltávolítása (kapcsolat megszüntetése)
   */
  async removeMeterPoint(meterPointId: string): Promise<boolean> {
    try {
      console.log('🗑️ Mérőhely eltávolítása, ID:', meterPointId);
      
      // Töröljük a meter_point dokumentumot
      const docRef = doc(db, 'meter_points', meterPointId);
      await deleteDoc(docRef);
      
      console.log('✅ Mérőhely sikeresen eltávolítva');
      return true;
      
    } catch (error) {
      console.error('❌ Mérőhely eltávolítási hiba:', error);
      throw error;
    }
  }

  /**
   * Mérőhely hozzárendelése ügyfélhez
   * (Ez a Python backend-ben is megtörténik, de frontend-ről is lehet)
   */
  async linkMeterPointToCustomer(
    podId: string,
    customerId: string,
    consumptionLocationId?: string
  ): Promise<boolean> {
    try {
      console.log('🔗 POD hozzárendelés indítása:', {
        podId,
        customerId,
        consumptionLocationId
      });

      // Keressük meg a meter_point-ot
      const meterPoint = await this.getMeterPointByPod(podId);
      console.log('📍 Meglévő meter_point:', meterPoint ? `Találat (ID: ${meterPoint.id})` : 'Nincs');
      
      if (meterPoint) {
        // Frissítjük a meglévőt
        console.log('🔄 Meglévő meter_point frissítése...');
        const docRef = doc(db, 'meter_points', meterPoint.id);
        await updateDoc(docRef, {
          customerId,
          consumptionLocationId: consumptionLocationId || null,
          updatedAt: Timestamp.now()
        });
        console.log('✅ Meter_point frissítve');
      } else {
        // Létrehozunk egy újat
        console.log('➕ Új meter_point létrehozása...');
        const newDoc = await addDoc(collection(db, 'meter_points'), {
          podId,
          customerId,
          consumptionLocationId: consumptionLocationId || null,
          serialNumber: '',
          energyType: 'electricity',
          multiplier: 1.0,
          provider: '',
          isActive: true,
          isBillable: true,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
        console.log('✅ Új meter_point létrehozva, ID:', newDoc.id);
      }
      
      // Frissítjük az energy_pods kollekciót is
      console.log('🔍 Energy_pods frissítése...');
      const energyPodRef = doc(db, POD_COLLECTION, podId);
      const energyPodSnap = await getDoc(energyPodRef);
      
      if (energyPodSnap.exists()) {
        console.log('📝 Energy_pods dokumentum létezik, frissítés...');
        await updateDoc(energyPodRef, {
          customerId,
          'customer_info.customer_id': customerId,
          last_update: Timestamp.now()
        });
        console.log('✅ Energy_pods frissítve');
      } else {
        console.log('⚠️ Energy_pods dokumentum NEM létezik POD ID-val:', podId);
      }
      
      console.log('🎉 POD hozzárendelés sikeresen befejezve!');
      return true;
      
    } catch (error) {
      console.error('❌ POD hozzárendelési hiba:', error);
      throw error;
    }
  }
}

export const meterReadingService = new MeterReadingService();
export default meterReadingService;
